import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

interface FullscreenButtonProps {
  isInline?: boolean;
}

/**
 * Robust Fullscreen toggle button.
 * CRITICAL: The toggle function must be synchronous to preserve the
 * browser's "user gesture" requirement for fullscreen API.
 */
export const FullscreenButton: React.FC<FullscreenButtonProps> = ({ isInline = false }) => {
  const { t } = useTranslation();
  const [isFullscreen, setIsFullscreen] = useState(false);

  const getFullscreenState = useCallback((): boolean => {
    const doc = document as any;
    return !!(
      doc.fullscreenElement ||
      doc.webkitFullscreenElement ||
      doc.mozFullScreenElement ||
      doc.msFullscreenElement
    );
  }, []);

  const syncState = useCallback(() => {
    setIsFullscreen(getFullscreenState());
  }, [getFullscreenState]);

  useEffect(() => {
    syncState();
    const events = [
      "fullscreenchange",
      "webkitfullscreenchange",
      "mozfullscreenchange",
      "MSFullscreenChange",
    ];
    events.forEach((e) => document.addEventListener(e, syncState));
    return () => events.forEach((e) => document.removeEventListener(e, syncState));
  }, [syncState]);

  // MUST be synchronous — no async/await — to preserve user gesture
  const toggleFullscreen = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const isFull = getFullscreenState();

    if (!isFull) {
      // Enter fullscreen — call directly, synchronously
      const el = document.documentElement as any;
      const fn =
        el.requestFullscreen ||
        el.webkitRequestFullscreen ||
        el.mozRequestFullScreen ||
        el.msRequestFullscreen;
      if (fn) {
        fn.call(el)?.catch?.(() => {});
      }
    } else {
      // Exit fullscreen
      const doc = document as any;
      const fn =
        doc.exitFullscreen ||
        doc.webkitExitFullscreen ||
        doc.mozCancelFullScreen ||
        doc.msExitFullscreen;
      if (fn) {
        fn.call(doc)?.catch?.(() => {});
      }
    }
  };

  if (!isInline) {
    return (
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={toggleFullscreen}
        title={isFullscreen ? t("common.fullscreen.exit") : t("common.fullscreen.enter")}
        className="fixed top-4 right-4 z-[9999] shadow-lg"
      >
        <i
          className={`bx ${isFullscreen ? "bx-exit-fullscreen" : "bx-fullscreen"}`}
          style={{ fontSize: "1.25rem" }}
        ></i>
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      onClick={toggleFullscreen}
      title={isFullscreen ? t("common.fullscreen.exit") : t("common.fullscreen.enter")}
    >
      <i
        className={`bx ${isFullscreen ? "bx-exit-fullscreen" : "bx-fullscreen"}`}
        style={{ fontSize: "1.25rem" }}
      ></i>
    </Button>
  );
};
